import { useEffect, useRef, useState, useCallback } from "react";

import axios from "axios";
import { io, Socket } from "socket.io-client";

import { ApplicationEndpoints } from "@generated/application";
import { apiBaseUrls } from "@platform/constants/shared-constants";

import { useStackRealtime } from "./useStackRealtime";

// ── Types (mirror backend k8s-event.types.ts) ─────────────────────────────────

export interface K8sEventSnapshot {
  name: string;
  namespace: string;
  involvedObjectName: string;
  involvedObjectKind: string;
  reason: string;
  message: string;
  type: "Normal" | "Warning";
  count: number;
  firstTimestamp: string | null;
  lastTimestamp: string | null;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export interface UseK8sEventsResult {
  events: K8sEventSnapshot[];
  loading: boolean;
  error: string | null;
  connected: boolean;
  refresh: () => void;
}

/**
 * Real-time K8s events hook.
 *
 * 1. On mount: fetches initial events via `GET /api/v1/applications/:alias/events`
 * 2. Opens a Socket.io connection to `/pods` namespace (websocket transport only)
 * 3. Subscribes via `subscribeEvents` — backend streams K8sEventSnapshot[] on each watch event
 * 4. Socket.io auto-reconnects on disconnect — no polling fallback needed
 * 5. On unmount: disconnects the socket
 *
 * When inside a StackRealtimeProvider, returns shared context data
 * instead of opening its own WebSocket connection.
 */
export function useK8sEvents(alias: string | undefined): UseK8sEventsResult {
  // ── Context consumption ──────────────────────────────────────────────────
  const ctx = useStackRealtime();

  // ── Fallback: always declare hooks to satisfy rules-of-hooks ──────────
  const [events, setEvents] = useState<K8sEventSnapshot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);

  const fetchEvents = useCallback(async () => {
    if (!alias) return;
    setLoading(true);
    setError(null);
    try {
      const url = `${apiBaseUrls.BACKEND}${ApplicationEndpoints.GET_APPLICATION_EVENTS.replace(":id", alias)}`;
      const res = await axios.get<K8sEventSnapshot[]>(url, {
        withCredentials: true,
      });
      setEvents(res.data);
    } catch {
      setError("Failed to load events.");
    } finally {
      setLoading(false);
    }
  }, [alias]);

  useEffect(() => {
    if (!alias) return;

    // If inside a StackRealtimeProvider, don't open a WebSocket —
    // the SSE context provides all real-time data.
    if (ctx) return;

    void fetchEvents();

    // Open WebSocket — connect to the /pods namespace
    const socket = io(`${apiBaseUrls.BACKEND}/pods`, {
      withCredentials: true,
      transports: ["websocket"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 20,
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      setConnected(true);
      socket.emit("subscribeEvents", { alias });
    });

    socket.on("events", (data: K8sEventSnapshot[]) => {
      setEvents(data);
      setError(null);
    });

    socket.on("error", (data: { message: string }) => {
      setError(data.message);
    });

    socket.on("disconnect", () => {
      setConnected(false);
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [alias, fetchEvents, ctx]);

  // ── Context short-circuit: return shared data if available ──────────────
  if (ctx) {
    return {
      events: ctx.k8sEvents,
      loading: ctx.k8sEventsLoading,
      error: ctx.k8sEventsError,
      connected: ctx.podsConnected,
      refresh: ctx.refetchK8sEvents,
    };
  }

  return { events, loading, error, connected, refresh: fetchEvents };
}
